/**
 * Viewer toolbar: overlay of toggle buttons and controls that sits on top of
 * the 3D canvas. Pure UI — no Three.js rendering logic.
 *
 * Toggles are organised into three collapsible accordion groups:
 *   • Rendering  (Layers icon)   — background, views, wireframe, polygon, excluded
 *   • Lighting   (Sun icon)      — moving light, SSAO, bloom, soft shadows
 *   • View       (Compass icon)  — inspector, measurement, presentation, axis, grid, camera
 */

import { useState, type RefObject } from 'react';
import { Trash2, Layers, Sun, Compass, Play } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { ExcludedToggle } from '@/components/viewer/ExcludedToggle';
import { GhostToggle } from '@/components/viewer/GhostToggle';
import { OrthographicPerspectiveToggle } from '@/components/viewer/OrthographicPerspectiveToggle';
import { GridToggle } from '@/components/viewer/GridToggle';
import { AxisToggle } from '@/components/viewer/AxisToggle';
import { BackgroundColorPicker } from '@/components/viewer/BackgroundColorPicker';
import { WireframeToggle } from '@/components/viewer/WireframeToggle';
import { PolygonModeToggle } from '@/components/viewer/PolygonModeToggle';
import { MovingLightToggle } from '@/components/viewer/MovingLightToggle';
import { LightAnimationPauseToggle } from '@/components/viewer/LightAnimationPauseToggle';
import { PresentationModeToggle } from '@/components/viewer/PresentationModeToggle';
import { SSAOToggle } from '@/components/viewer/SSAOToggle';
import { BloomToggle } from '@/components/viewer/BloomToggle';
import { SoftShadowsToggle } from '@/components/viewer/SoftShadowsToggle';
import { MeasurementToggle } from '@/components/viewer/MeasurementToggle';
import { InspectorToggle } from '@/components/viewer/InspectorToggle';
import { MultiViewToggle } from '@/components/viewer/MultiViewToggle';
import { PiPToggle } from '@/components/viewer/PiPToggle';
import { GrayscaleToggle } from '@/components/viewer/GrayscaleToggle';
import { AnnotationsToggle } from '@/components/viewer/AnnotationsToggle';
import { ToolbarGroup } from '@/components/viewer/toolbar/ToolbarGroup';
import type { MeasurementSystemHandle } from '@/components/viewer/MeasurementSystem';

export interface ViewerState {
  isOrthographic: boolean;
  showGrid: boolean;
  showAxis: boolean;
  backgroundColor: string;
  wireframeOpacity: number;
  showMovingLight: boolean;
  presentationMode: boolean;
  measurementEnabled: boolean;
  measurementCount: number;
  inspectorEnabled: boolean;
  showExcluded: boolean;
  multiView: boolean;
  /** Whether PiP camera overlays are visible (only relevant when pipCameras.length > 0) */
  showPiP: boolean;
  /** When true, all object colors are replaced with their luminance-equivalent gray and
   *  materials are set to flat/non-reflective for pure geometry debugging. */
  grayscaleMode: boolean;
  /** When true, all triangle/polygon edges are overlaid on top of the solid mesh. */
  polygonMode: boolean;
  /** When true, object annotation overlays are shown when annotations are defined. */
  annotationsEnabled: boolean;
  /** When true, ghost (%) objects are rendered; when false they are hidden but
   *  constraint checking still runs against the last compiled ghost geometry. */
  showGhosts: boolean;
  /** When true, all light orbit animations are paused at their current position. */
  lightAnimationsPaused: boolean;
}

export const DEFAULT_VIEWER_STATE: ViewerState = {
  isOrthographic: false,
  showGrid: true,
  showAxis: false,
  backgroundColor: '#6b6b6d',
  wireframeOpacity: 0,
  showMovingLight: false,
  presentationMode: false,
  measurementEnabled: false,
  measurementCount: 0,
  inspectorEnabled: false,
  showExcluded: true, // Default to showing excluded parts (preview mode)
  multiView: false,
  showPiP: true,
  grayscaleMode: false,
  polygonMode: false,
  annotationsEnabled: true,
  showGhosts: true,
  lightAnimationsPaused: false,
};

type GroupId = 'rendering' | 'lighting' | 'view';

interface ViewerToolbarProps {
  state: ViewerState;
  onStateChange: (updater: (prev: ViewerState) => ViewerState) => void;
  /** SSAO / Bloom toggles go through context, so we expose callbacks */
  ssaoEnabled: boolean;
  onSSAOToggle: (enabled: boolean) => void;
  bloomEnabled: boolean;
  onBloomToggle: (enabled: boolean) => void;
  softShadowsEnabled: boolean;
  onSoftShadowsToggle: (enabled: boolean) => void;
  measurementRef: RefObject<MeasurementSystemHandle | null>;
  /** When true, show only minimal controls (presentation toggle) */
  minimal?: boolean;
  /** When true, hide all toolbar UI (showcase/landing-page mode) */
  showcaseMode?: boolean;
  /** When true, the background color is driven by scene_background() in code */
  backgroundColorOverridden?: boolean;
  /** When true, the PiP toggle is shown (at least one camera_pip() is defined) */
  hasPiPCameras?: boolean;
  /** When true, the annotations toggle is shown (at least one annotation() is defined) */
  hasAnnotations?: boolean;
  /** When true, the ghost toggle is shown (at least one % ghost object is in the scene) */
  hasGhosts?: boolean;
  /** When true, at least one custom light has an orbit — shows the pause toggle */
  hasOrbitingLights?: boolean;
  /** When true, at least one custom light has LFO bindings — also shows the pause toggle */
  hasLFOLights?: boolean;
  /** When true, at least one geometry has a motion() wrapper — also shows the pause toggle */
  hasMotionGeometries?: boolean;
  /** When true, a camera_path() intro sequence is defined — shows the replay button */
  hasCameraPath?: boolean;
  /** Called when the user clicks the camera path replay button */
  onReplayCameraPath?: () => void;
}

export function ViewerToolbar({
  state,
  onStateChange,
  ssaoEnabled,
  onSSAOToggle,
  bloomEnabled,
  onBloomToggle,
  softShadowsEnabled,
  onSoftShadowsToggle,
  measurementRef,
  minimal = false,
  showcaseMode = false,
  backgroundColorOverridden = false,
  hasPiPCameras = false,
  hasAnnotations = false,
  hasGhosts = false,
  hasOrbitingLights = false,
  hasLFOLights = false,
  hasMotionGeometries = false,
  hasCameraPath = false,
  onReplayCameraPath,
}: ViewerToolbarProps) {
  const set = <K extends keyof ViewerState>(key: K, value: ViewerState[K]) =>
    onStateChange((prev) => ({ ...prev, [key]: value }));

  // Accordion state: only one group open at a time; all start collapsed.
  const [openGroup, setOpenGroup] = useState<GroupId | null>(null);

  const toggleGroup = (id: GroupId) =>
    setOpenGroup((prev) => (prev === id ? null : id));

  // Active-item dot logic per group
  const renderingActive =
    state.wireframeOpacity > 0 ||
    state.polygonMode ||
    state.multiView ||
    state.showPiP ||
    state.backgroundColor !== DEFAULT_VIEWER_STATE.backgroundColor ||
    !state.showExcluded ||
    !state.showGhosts ||
    state.grayscaleMode;

  const lightingActive =
    state.showMovingLight ||
    state.lightAnimationsPaused ||
    ssaoEnabled ||
    bloomEnabled ||
    softShadowsEnabled;

  const viewActive =
    state.inspectorEnabled ||
    state.measurementEnabled ||
    state.presentationMode ||
    state.showAxis ||
    !state.showGrid ||
    state.isOrthographic;

  // Showcase mode: no UI at all
  if (showcaseMode) {
    return null;
  }

  // Minimal mode: only show presentation toggle
  if (minimal) {
    return (
      <div className="absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-background/60 px-1.5 py-0.5 backdrop-blur-sm">
        <PresentationModeToggle
          isEnabled={state.presentationMode}
          onToggle={(v) => set('presentationMode', v)}
        />
      </div>
    );
  }

  return (
    <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-lg bg-background/60 px-1.5 py-1 backdrop-blur-sm">
      {/* ── Camera path replay button — shown when camera_path() is defined ── */}
      {hasCameraPath && onReplayCameraPath && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onReplayCameraPath}
                className="flex h-7 w-7 items-center justify-center rounded text-foreground opacity-70 transition-opacity hover:opacity-100"
                aria-label="Replay intro camera path"
              >
                <Play className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="border-border bg-background text-foreground"
            >
              <p>Replay intro</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* ── Rendering group ── */}
      <ToolbarGroup
        icon={Layers}
        label="Rendering"
        isOpen={openGroup === 'rendering'}
        onToggle={() => toggleGroup('rendering')}
        hasActiveItem={renderingActive}
      >
        <BackgroundColorPicker
          color={state.backgroundColor}
          onChange={(v) => set('backgroundColor', v)}
          overridden={backgroundColorOverridden}
        />
        <MultiViewToggle
          isEnabled={state.multiView}
          onToggle={(v) => set('multiView', v)}
        />
        {hasPiPCameras && (
          <PiPToggle
            isEnabled={state.showPiP}
            onToggle={(v) => set('showPiP', v)}
          />
        )}
        <WireframeToggle
          wireframeOpacity={state.wireframeOpacity}
          onOpacityChange={(v) => set('wireframeOpacity', v)}
        />
        <PolygonModeToggle
          isEnabled={state.polygonMode}
          onToggle={(v) => set('polygonMode', v)}
        />
        <ExcludedToggle
          showExcluded={state.showExcluded}
          onToggle={(v) => set('showExcluded', v)}
        />
        <GhostToggle
          showGhosts={state.showGhosts}
          onToggle={(v) => set('showGhosts', v)}
          hasGhosts={hasGhosts}
        />
        <GrayscaleToggle
          isEnabled={state.grayscaleMode}
          onToggle={(v) => set('grayscaleMode', v)}
        />
      </ToolbarGroup>

      {/* ── Lighting & Effects group ── */}
      <ToolbarGroup
        icon={Sun}
        label="Lighting & Effects"
        isOpen={openGroup === 'lighting'}
        onToggle={() => toggleGroup('lighting')}
        hasActiveItem={lightingActive}
      >
        <MovingLightToggle
          isEnabled={state.showMovingLight}
          onToggle={(v) => set('showMovingLight', v)}
        />
        {(hasOrbitingLights || hasLFOLights || hasMotionGeometries) && (
          <LightAnimationPauseToggle
            isPaused={state.lightAnimationsPaused}
            onToggle={(v) => set('lightAnimationsPaused', v)}
          />
        )}
        <SSAOToggle isEnabled={ssaoEnabled} onToggle={onSSAOToggle} />
        <BloomToggle isEnabled={bloomEnabled} onToggle={onBloomToggle} />
        <SoftShadowsToggle
          isEnabled={softShadowsEnabled}
          onToggle={onSoftShadowsToggle}
        />
      </ToolbarGroup>

      {/* ── View group ── */}
      <ToolbarGroup
        icon={Compass}
        label="View"
        isOpen={openGroup === 'view'}
        onToggle={() => toggleGroup('view')}
        hasActiveItem={viewActive}
      >
        {hasAnnotations && (
          <AnnotationsToggle
            isEnabled={state.annotationsEnabled}
            onToggle={(v: boolean) => set('annotationsEnabled', v)}
          />
        )}
        <InspectorToggle
          isEnabled={state.inspectorEnabled}
          onToggle={(v) => set('inspectorEnabled', v)}
        />
        <MeasurementToggle
          isEnabled={state.measurementEnabled}
          onToggle={(v) => set('measurementEnabled', v)}
        />
        {state.measurementEnabled && state.measurementCount > 0 && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => measurementRef.current?.clearAll()}
                  className="flex h-7 w-7 items-center justify-center rounded text-foreground opacity-70 transition-opacity hover:opacity-100"
                  aria-label="Clear all measurements"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="border-border bg-background text-foreground"
              >
                <p>Clear Measurements</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <PresentationModeToggle
          isEnabled={state.presentationMode}
          onToggle={(v) => set('presentationMode', v)}
        />
        <AxisToggle
          showAxis={state.showAxis}
          onToggle={(v) => set('showAxis', v)}
        />
        <GridToggle
          showGrid={state.showGrid}
          onToggle={(v) => set('showGrid', v)}
        />
        <OrthographicPerspectiveToggle
          isOrthographic={state.isOrthographic}
          onToggle={(v) => set('isOrthographic', v)}
        />
      </ToolbarGroup>
    </div>
  );
}
